import React, { useEffect, useState, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { FiX } from 'react-icons/fi';
import './SharedModal.css';

const ANIMATION_DURATION_MS = 250;

export type SharedModalSize = 'sm' | 'md' | 'lg' | 'xl' | 'full';
export type SharedModalVariant = 'default' | 'confirm' | 'danger';

export interface SharedModalProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  children?: React.ReactNode;
  footer?: React.ReactNode;
  size?: SharedModalSize;
  variant?: SharedModalVariant;
  closeOnOverlayClick?: boolean;
  showCloseButton?: boolean;
  /** Custom class for the modal container */
  className?: string;
  /** Optional class on the fixed overlay (e.g. z-index above page chrome) */
  overlayClassName?: string;
  /** Hide header when no title and no close button */
  hideHeader?: boolean;
}

const SharedModal: React.FC<SharedModalProps> = ({
  open,
  onClose,
  title,
  children,
  footer,
  size = 'md',
  variant = 'default',
  closeOnOverlayClick = true,
  showCloseButton = true,
  className = '',
  overlayClassName = '',
  hideHeader = false,
}) => {
  const [isClosing, setIsClosing] = useState(false);
  const [isMounted, setIsMounted] = useState(false);

  const handleClose = useCallback(() => {
    if (isClosing) return;
    setIsClosing(true);
    setTimeout(() => {
      onClose();
      setIsClosing(false);
      setIsMounted(false);
    }, ANIMATION_DURATION_MS);
  }, [onClose, isClosing]);

  useEffect(() => {
    if (open) {
      setIsMounted(true);
    } else {
      setIsMounted(false);
      setIsClosing(false);
    }
  }, [open]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && open) {
        handleClose();
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [open, handleClose]);

  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && closeOnOverlayClick) {
      handleClose();
    }
  };

  if (!open && !isClosing) return null;

  const showHeader = !hideHeader && (title || showCloseButton);

  const content = (
    <div
      className={`shared-modal-overlay ${overlayClassName} ${isClosing ? 'shared-modal-overlay--closing' : ''}`.trim()}
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? 'shared-modal-title' : undefined}
    >
      <div
        className={`shared-modal shared-modal--${size} shared-modal--${variant} ${isClosing ? 'shared-modal--closing' : ''} ${className}`}
        onClick={e => e.stopPropagation()}
      >
        {showHeader && (
          <div className="shared-modal__header">
            {title && (
              <h2 id="shared-modal-title" className="shared-modal__title">
                {title}
              </h2>
            )}
            {showCloseButton && (
              <button
                type="button"
                className="shared-modal__close"
                onClick={handleClose}
                aria-label="Fechar"
              >
                <FiX size={20} />
              </button>
            )}
          </div>
        )}
        {children && <div className="shared-modal__body">{children}</div>}
        {footer && <div className="shared-modal__footer">{footer}</div>}
      </div>
    </div>
  );

  return ReactDOM.createPortal(content, document.body);
};

export default SharedModal;
